import { useState } from "react";
import {
  ChevronDown, Share2, LogOut, ArrowRight, Play, Copy, Check, Plug,
  Film, Cog, Server, Atom, Database, Sparkles, MonitorPlay, RefreshCw,
  Package, Cpu, Palette,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useAirline } from "../context/AirlineContext";
import PalWelcome from "../slides/pal/PalWelcome";
import { DemoPreviewModal } from "../slides/SlideExploreDemos";
import PalTeam from "../slides/pal/PalTeam";
import PalServices from "../slides/pal/PalServices";
import PalDelivery from "../slides/pal/PalDelivery";
import PalMetadata from "../slides/pal/PalMetadata";
import PalContent from "../slides/pal/PalContent";
import PalAdvertising from "../slides/pal/PalAdvertising";
import PalCommercials from "../slides/pal/PalCommercials";
import PalNextSteps from "../slides/pal/PalNextSteps";

const PAL_BEFORE_DEMOS = [["pal-team", PalTeam], ["pal-services", PalServices], ["pal-delivery", PalDelivery], ["pal-metadata", PalMetadata], ["pal-content", PalContent]];
const PAL_AFTER_DEMOS = [["pal-advertising", PalAdvertising], ["pal-commercials", PalCommercials], ["pal-next-steps", PalNextSteps]];

const PalMobileBlock = ({ id, children }) => {
  const { isDark } = useTheme();
  return (
    <div id={id} data-testid={`mobile-${id}`} className={`px-6 py-14 border-t ${isDark ? "border-white/10" : "border-black/10"}`}>
      {children}
    </div>
  );
};

const LOGO_DARK = "https://customer-assets.emergentagent.com/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/h1sxdkcz_Aeroplay%20logo%202026_White.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/oot4cqoo_Aeroplay%20logo%202026_Black.png";
const NUCLEUS_BRIDGE = "https://customer-assets-lxgj4vgw.emergentagent.net/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/0zo9n6c8_NucluesOS_Brand-11.webp";
const NUCLEUS_MAX = "https://customer-assets-lxgj4vgw.emergentagent.net/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/6mvq60gx_NucluesOS_Brand-13.webp";
const NUCLEUS_LENS = "https://customer-assets-lxgj4vgw.emergentagent.net/job_24108f3e-b858-4890-993f-f1e1a60b89e6/artifacts/5sciu7sm_NucluesOS_Brand-12.webp";
const CATHAY_LOGO = "/assets/cathay_logo.png";

const BRIDGE_COLOR = "#A855F7";
const MAX_COLOR = "#EF4444";
const LENS_COLOR = "#22C55E";

const outcomes = [
  { value: "2016", label: "Founded in Singapore" },
  { value: "50+", label: "Aeroplay staff globally" },
  { value: "500+", label: "Group staff worldwide" },
  { value: "40+", label: "Airlines served" },
];

const problems = [
  { title: "Long content cycles.", detail: "Months to update. Passengers see stale libraries." },
  { title: "Expensive supply chain.", detail: "High cost per title. Vendors stacked on vendors." },
  { title: "Fragmented delivery.", detail: "Studios, encoders, QC and OEMs complexity." },
];

const realProblemTags = ["Satisfaction", "Loyalty", "Monetisation", "Personalisation", "Data connectivity", "Real-time engagement"];

const pillars = [
  { key: "fast", label: "Fast", tag: "Media", color: "#F2A900", one: "Studio to seat in one closed loop.", detail: "AI-guided discovery, a TPN-Gold lab and one workflow that learns from every flight." },
  { key: "lean", label: "Lean", tag: "Platform", color: "#10B981", one: "Same quality. Less spend.", detail: "Shared infrastructure and AI-assisted operations that cut total cost of IFE without cutting corners." },
  { key: "personal", label: "Personal", tag: "Entertainment", color: "#A855F7", one: "Made for every route, every rider.", detail: "Content curated by region and demographic — branded to feel unmistakably yours." },
  { key: "intelligent", label: "Intelligent", tag: "Engagement", color: "#3B82F6", one: "AI that curates, learns and adapts.", detail: "NucleusOS behind the scenes, DARE in front of the passenger. One intelligence layer." },
];

const stack = [
  {
    id: "core", label: "Core", caption: "Studio-grade content ops", color: "#F2A900",
    modules: [
      { icon: Film, name: "Content Sourcing", desc: "Studios, OTT and regional distributors" },
      { icon: Cog, name: "AeroLab", desc: "Encoding, QC and metadata (TPN Gold)" },
      { icon: Server, name: "Delivery", desc: "IFE systems and AeroHub" },
    ],
  },
  {
    id: "nucleus", label: "NucleusOS", caption: "The intelligence layer", color: "#3B82F6",
    modules: [
      { icon: Atom, name: "Discovery", desc: "AI curation across catalogues" },
      { icon: Database, name: "Data", desc: "Route, cabin, passenger telemetry" },
      { icon: Sparkles, name: "Recommendation (DARE)", desc: "Real-time preference learning" },
    ],
  },
  {
    id: "experience", label: "Experience", caption: "The passenger surface", color: "#A855F7",
    modules: [
      { icon: MonitorPlay, name: "IFE Minisite & OTT", desc: "Branded surface, seat-back and BYOD" },
      { icon: RefreshCw, name: "Feedback loop", desc: "Every flight teaches the system" },
    ],
  },
];

const commercialLayers = [
  {
    id: "core", label: "Operational Services", pricingHow: "usage / cycle-based",
    color: "#FFD700", icon: Package, description: "Essential CSP foundation for content operations",
    items: [
      { name: "Licensing", pricing: "per title or package" },
      { name: "Technical", pricing: "per title or package" },
      { name: "Integration", pricing: "per cycle" },
      { name: "Acc. Management", pricing: "per cycle" },
    ],
  },
  {
    id: "platform", label: "Recurring SaaS", pricingHow: "monthly subscriptions",
    color: "#3B82F6", icon: Cpu, description: "Digital infrastructure and intelligence layer",
    items: [
      { name: "NucleusOS", pricing: "monthly fee" },
      { name: "AI Rec. Engine", pricing: "monthly fee" },
      { name: "IFE Platform", pricing: "per tail / per month" },
    ],
  },
  {
    id: "specialized", label: "Project-based", pricingHow: "scoped project pricing",
    color: "#A855F7", icon: Palette, description: "Bespoke solutions and custom projects",
    items: [
      { name: "Bespoke UI/UX", pricing: "scoped project" },
      { name: "Marketing", pricing: "scoped project" },
      { name: "Custom Projects", pricing: "scoped project" },
      { name: "Innovation Work", pricing: "scoped project" },
    ],
  },
];

const demos = [
  { id: "bridge", logo: NUCLEUS_BRIDGE, href: "https://bridge.nucleus-alpha.com/", accent: BRIDGE_COLOR, pill: "LIVE" },
  { id: "max", logo: NUCLEUS_MAX, href: "https://entertainment.nucleus-alpha.com", accent: MAX_COLOR, pill: "LIVE" },
  { id: "lens", logo: NUCLEUS_LENS, href: "https://lens.nucleus-alpha.com/report/019d7177-b755-77e1-8ffd-0b4ad6234444", accent: LENS_COLOR, pill: "LIVE" },
];

const withAlpha = (hex, alpha) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const MobileJourney = () => {
  const { isDark } = useTheme();
  const { airline } = useAirline();
  const isPrivate = airline && airline.id !== "default";
  const pal = isPrivate && !!airline.bespokeMobile && !!airline.content?.welcome;
  const accent = airline?.brandColor || "#F2A900";
  const [openPillar, setOpenPillar] = useState("fast");
  const [shareToast, setShareToast] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [previewDemo, setPreviewDemo] = useState(null);

  const handleShare = async () => {
    const isOnPitchRoute = /^\/pitch(\/.*)?$/.test(window.location.pathname);
    const url = isOnPitchRoute ? `${window.location.origin}${window.location.pathname}` : "https://aeroplay.app/";
    const text = isOnPitchRoute
      ? "Your Aeroplay presentation is available here. Please use the password provided separately."
      : "A quick look at Aeroplay.";
    try {
      if (navigator.share) { await navigator.share({ title: "Aeroplay", text, url }); return; }
      await navigator.clipboard.writeText(url);
      setShareToast("Link copied");
      setTimeout(() => setShareToast(""), 1800);
    } catch {}
  };

  const handleLogout = async () => {
    if (!isPrivate || !airline.code) return;
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/pitch/${airline.code}/logout`, { method: "POST", credentials: "include" });
    } catch {}
    window.location.href = "/";
  };

  const copyLink = async (id, href, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(href);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {}
  };

  const bg = isDark ? "bg-[#0b0b0d]" : "bg-[#fbfbf9]";
  const ink = isDark ? "text-white" : "text-[#1d1d1f]";
  const muted = isDark ? "text-white/60" : "text-black/55";
  const dim = isDark ? "text-white/45" : "text-black/45";
  const soft = isDark ? "bg-white/[0.03] border-white/10" : "bg-white border-black/10";
  const hairline = isDark ? "border-white/10" : "border-black/10";
  const sectionKicker = isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)";

  return (
    <div className={`min-h-screen w-full ${bg} ${ink}`} data-testid="mobile-journey" style={{ letterSpacing: "-0.01em" }}>
      <style>{`
        @keyframes mfade { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .m-in { animation: mfade 0.5s cubic-bezier(0.22, 1, 0.36, 1); }
      `}</style>

      {/* Header — Aeroplay wordmark, backdrop-blurred */}
      <header className={`sticky top-0 z-30 ${isDark ? "bg-[#0b0b0d]/85" : "bg-[#fbfbf9]/85"} border-b ${hairline}`} style={{ backdropFilter: "saturate(180%) blur(20px)", WebkitBackdropFilter: "saturate(180%) blur(20px)" }}>
        <div className="px-5 h-12 flex items-center justify-between" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
          <img
            src={isDark ? LOGO_DARK : LOGO_LIGHT}
            alt="Aeroplay"
            className="h-8 w-auto object-contain"
          />
          <div className="flex items-center gap-1">
            <button
              onClick={handleShare}
              data-testid="mobile-share-btn"
              className={`w-8 h-8 rounded-full flex items-center justify-center ${muted} ${isDark ? "hover:bg-white/[0.08]" : "hover:bg-black/[0.05]"}`}
              aria-label="Share"
            >
              <Share2 className="w-4 h-4" />
            </button>
            {isPrivate && (
              <button
                onClick={handleLogout}
                data-testid="mobile-logout-btn"
                className={`w-8 h-8 rounded-full flex items-center justify-center ${muted} ${isDark ? "hover:bg-white/[0.08]" : "hover:bg-black/[0.05]"}`}
                aria-label="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {shareToast && <div className={`text-center pb-2 text-[11px] ${muted}`}>{shareToast}</div>}
      </header>

      {/* Hero */}
      {pal && (
        <>
          <PalWelcome airline={airline} />
        </>
      )}
      {!pal && (
      <section className="px-6 pt-14 pb-16 m-in">
        <div className="k-eyebrow" style={{ color: accent }}>
          {isPrivate ? `Private Pitch · For ${airline.name}` : "A premium content service provider"}
        </div>
        <h1 className={`mt-4 font-semibold tracking-tight ${ink}`} style={{ fontSize: 42, lineHeight: 1.05, letterSpacing: "-0.03em" }}>
          Content, operations
          <br />and passengers.
          <br /><span style={{ color: accent }}>Made effortless.</span>
        </h1>
        <p className={`mt-6 text-[17px] leading-relaxed ${muted}`}>
          Aeroplay connects studio-grade content, an AI-driven operating system and cabin-ready experiences into one story your passengers actually feel.
        </p>

        <div className="mt-8 flex items-center gap-2">
          <a
            href="#who-we-are"
            data-testid="mobile-explore-btn"
            className={`inline-flex items-center gap-2 h-11 px-5 rounded-full text-[14px] font-semibold ${isDark ? "bg-white text-[#0b0b0d]" : "bg-[#1d1d1f] text-white"}`}
          >
            Explore <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>
      )}

      {/* Who we are */}
      <section id="who-we-are" className={`px-6 py-14 border-t ${hairline}`}>
        <div className="k-eyebrow" style={{ color: accent }}>Who we are</div>
        <h2 className={`mt-3 font-semibold tracking-tight ${ink}`} style={{ fontSize: 32, lineHeight: 1.08, letterSpacing: "-0.028em" }}>
          Aeroplay Entertainment.
          <br /><span style={{ color: accent }}>Fast. Lean. Personal. Intelligent.</span>
        </h2>
        <p className={`mt-5 text-[16px] leading-relaxed ${muted}`}>
          An independent Content Service Provider inside a media ecosystem — digital workflows from studio to seat, with an obsessive focus on speed, cost and personalisation.
        </p>
        <dl className={`mt-8 pt-6 grid grid-cols-2 gap-5 border-t ${hairline}`}>
          {outcomes.map((o) => (
            <div key={o.value}>
              <dd className={`text-[28px] font-semibold tracking-tight leading-none ${ink}`}>{o.value}</dd>
              <dt className={`mt-1.5 text-[12px] ${muted}`}>{o.label}</dt>
            </div>
          ))}
        </dl>
      </section>

      {/* The problem */}
      {!pal && (
      <section className={`px-6 py-14 border-t ${hairline}`}>
        <div className="k-eyebrow" style={{ color: sectionKicker }}>The problem</div>
        <h2 className={`mt-3 font-semibold tracking-tight ${ink}`} style={{ fontSize: 30, lineHeight: 1.08, letterSpacing: "-0.028em" }}>
          IFE has a passenger
          <br /><span style={{ color: accent }}>engagement problem.</span>
        </h2>
        <p className={`mt-5 text-[15px] leading-relaxed ${muted}`}>
          Airlines invest heavily in hardware and content — yet passengers still don't feel seen. The gap is not the seat-back. It's the supply chain behind it.
        </p>
        <ol className={`mt-8 space-y-6 pt-6 border-t ${hairline}`}>
          {problems.map((p, i) => (
            <li key={p.title}>
              <div className="k-eyebrow" style={{ color: dim.replace("text-", "").includes("white") ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)" }}>0{i + 1}</div>
              <div className={`mt-2 text-[18px] font-semibold tracking-tight ${ink}`}>{p.title}</div>
              <p className={`mt-1.5 text-[14px] leading-relaxed ${muted}`}>{p.detail}</p>
            </li>
          ))}
        </ol>
        <div
          className="mt-8 rounded-2xl px-5 py-5 border"
          style={{ borderColor: "rgba(242,169,0,0.35)", backgroundColor: isDark ? "rgba(242,169,0,0.08)" : "rgba(242,169,0,0.10)" }}
        >
          <div className="k-eyebrow" style={{ color: "var(--k-accent)" }}>The real problem</div>
          <div className={`mt-2 text-[20px] font-semibold tracking-tight ${ink}`}>
            Knowing the passenger — and engaging with them.
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {realProblemTags.map((t) => (
              <span key={t} className="text-[11px] font-medium px-2.5 py-1 rounded-full" style={{ backgroundColor: "rgba(242,169,0,0.18)", color: "var(--k-accent-strong)" }}>
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Four Pillars */}
      <section id="pillars" className={`px-6 py-14 border-t ${hairline}`}>
        <div className="k-eyebrow" style={{ color: sectionKicker }}>The Aeroplay Way</div>
        <h2 className={`mt-3 font-semibold tracking-tight ${ink}`} style={{ fontSize: 32, lineHeight: 1.08, letterSpacing: "-0.028em" }}>
          Four pillars.<br /><span style={{ color: accent }}>One story.</span>
        </h2>
        <ul className={`mt-8 divide-y ${isDark ? "divide-white/10" : "divide-black/10"}`}>
          {pillars.map((p) => {
            const open = openPillar === p.key;
            return (
              <li key={p.key}>
                <button
                  onClick={() => setOpenPillar(open ? null : p.key)}
                  data-testid={`mobile-pillar-${p.key}`}
                  className="w-full py-5 text-left flex items-start gap-3"
                  style={{ minHeight: 60 }}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`text-[22px] font-semibold tracking-tight leading-tight ${ink}`}>{p.label}</div>
                    <div className="k-eyebrow mt-1" style={{ color: p.color }} data-testid={`mobile-pillar-${p.key}-tag`}>
                      {p.tag}
                    </div>
                    <div className={`mt-2 text-[15px] leading-snug ${muted}`}>{p.one}</div>
                    {open && <p className={`mt-3 text-[14px] leading-relaxed ${muted}`}>{p.detail}</p>}
                  </div>
                  <ChevronDown className={`w-4 h-4 flex-shrink-0 mt-3 transition-transform ${open ? "rotate-180" : ""} ${muted}`} />
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Our Platform */}
      {!pal && (
      <section className={`px-6 py-14 border-t ${hairline}`}>
        <div className="k-eyebrow" style={{ color: sectionKicker }}>Our platform</div>
        <h2 className={`mt-3 font-semibold tracking-tight ${ink}`} style={{ fontSize: 32, lineHeight: 1.08, letterSpacing: "-0.028em" }}>
          One system.<br /><span style={{ color: accent }}>Three layers.</span>
        </h2>
        <p className={`mt-5 text-[15px] leading-relaxed ${muted}`}>
          Content operations, NucleusOS intelligence and the passenger experience — connected end to end so each layer makes the next smarter.
        </p>

        <div className="mt-8 flex flex-col items-stretch gap-0">
          {stack.map((layer, idx) => (
            <div key={layer.id}>
              <div className={`rounded-2xl border overflow-hidden ${soft}`}>
                <div className={`px-4 pt-3 pb-3 border-b ${hairline}`} style={{ backgroundColor: withAlpha(layer.color, isDark ? 0.14 : 0.10) }}>
                  <div className="k-eyebrow" style={{ color: layer.color }}>{layer.label}</div>
                  <div className="mt-0.5 text-[15px] font-semibold tracking-tight" style={{ color: layer.color }}>
                    {layer.caption}
                  </div>
                </div>
                <ul className={`divide-y ${isDark ? "divide-white/10" : "divide-black/10"}`}>
                  {layer.modules.map((m) => {
                    const Icon = m.icon;
                    return (
                      <li key={m.name} className="px-4 py-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-4 h-4 flex-shrink-0" style={{ color: layer.color }} />
                          <span className={`text-[13px] font-semibold ${ink}`}>{m.name}</span>
                        </div>
                        <div className={`text-[12px] leading-snug pl-6 ${muted}`}>{m.desc}</div>
                      </li>
                    );
                  })}
                </ul>
              </div>
              {idx < stack.length - 1 && (
                <div className="flex items-center justify-center py-3" aria-hidden="true">
                  <div className="flex flex-col items-center">
                    <span className="w-[2px] h-3" style={{ backgroundColor: layer.color }} />
                    <span
                      className="w-7 h-7 rounded-full flex items-center justify-center border-2"
                      style={{ borderColor: withAlpha(layer.color, 0.5), background: isDark ? "#0b0b0d" : "#fbfbf9" }}
                    >
                      <Plug className="w-3.5 h-3.5" style={{ color: layer.color, transform: "rotate(-45deg)" }} />
                    </span>
                    <span className="w-[2px] h-3" style={{ backgroundColor: stack[idx + 1].color }} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
      )}

      {/* Commercial Model — private (airline) pitches only */}
      {isPrivate && !pal && (
        <section className={`px-6 py-14 border-t ${hairline}`}>
        <div className="k-eyebrow" style={{ color: sectionKicker }}>Commercial model</div>
        <h2 className={`mt-3 font-semibold tracking-tight ${ink}`} style={{ fontSize: 32, lineHeight: 1.08, letterSpacing: "-0.028em" }}>
          Three tiers.<br /><span style={{ color: accent }}>One partnership.</span>
        </h2>
        <p className={`mt-5 text-[15px] leading-relaxed ${muted}`}>
          Operational services, recurring platform and specialized projects — priced how each is best consumed.
        </p>
        <div className="mt-8 space-y-4">
          {commercialLayers.map((layer) => {
            const Icon = layer.icon;
            return (
              <div key={layer.id} className={`rounded-2xl border ${soft} p-5`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${layer.color}20` }}>
                    <Icon className="w-4.5 h-4.5" style={{ color: layer.color }} />
                  </div>
                  <div className="k-eyebrow" style={{ color: layer.color }}>{layer.label}</div>
                </div>
                <div className={`mt-1 text-[13px] ${muted}`}>{layer.description}</div>
                <ul className="mt-4 space-y-2">
                  {layer.items.map((item, i) => (
                    <li key={i} className={`rounded-xl px-3 py-2.5 flex items-center gap-2 ${isDark ? "bg-white/[0.03]" : "bg-black/[0.03]"}`}>
                      <span className={`text-[13px] font-medium flex-1 ${isDark ? "text-white/85" : "text-[#1d1d1f]"}`}>{item.name}</span>
                      <span
                        className="text-[9px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full flex-shrink-0"
                        style={{ backgroundColor: `${layer.color}20`, color: layer.color }}
                      >
                        {item.pricing}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className={`mt-4 pt-3 border-t flex items-center gap-2 ${hairline}`}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: layer.color }} />
                  <span className="text-[11px] font-semibold" style={{ color: layer.color }}>{layer.pricingHow}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      )}

      {pal && PAL_BEFORE_DEMOS.map(([id, C]) => <PalMobileBlock key={id} id={id}><C /></PalMobileBlock>)}

      {/* Explore Demos */}
      <section id="demos" className={`px-6 py-14 border-t ${hairline}`}>
        <div className="k-eyebrow" style={{ color: accent }}>Now, see it in action</div>
        <h2 className={`mt-3 font-semibold tracking-tight ${ink}`} style={{ fontSize: 32, lineHeight: 1.08, letterSpacing: "-0.028em" }}>
          Explore the<br /><span style={{ color: accent }}>Aeroplay product.</span>
        </h2>
        <p className={`mt-5 text-[15px] leading-relaxed ${muted}`}>
          Three live products, three angles on the same platform. Best viewed on desktop.
        </p>

        <div className="mt-8 space-y-4">
          {demos.map((d) => (
            <a
              key={d.id}
              href={d.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`mobile-demo-${d.id}`}
              onClick={(e) => { e.preventDefault(); setPreviewDemo(d); }}
              className={`block rounded-2xl border-2 overflow-hidden transition-all ${isDark ? "bg-white/[0.03]" : "bg-white"}`}
              style={{
                borderColor: withAlpha(d.accent, 0.55),
                boxShadow: `0 10px 30px -20px ${withAlpha(d.accent, 0.7)}`,
              }}
            >
              <div className={`flex items-center justify-center px-6 py-10 ${isDark ? "bg-black/20" : "bg-black/[0.02]"}`}>
                <img src={d.logo} alt="" className="max-h-28 w-full object-contain" />
              </div>
              <div className={`px-4 py-3 flex items-center justify-between gap-2 border-t ${hairline}`}>
                <div className="flex items-center gap-2 min-w-0">
                  {d.pill && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full"
                      style={{ backgroundColor: withAlpha("#22C55E", 0.18), color: "#16A34A" }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#22C55E" }} /> Live
                    </span>
                  )}
                  {d.useCase && (
                    <span
                      className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full flex-shrink-0"
                      style={{ backgroundColor: `${d.accent}20`, color: d.accent }}
                    >
                      Use case
                      <img
                        src={CATHAY_LOGO}
                        alt="Cathay"
                        className="h-3 w-auto object-contain"
                        style={{ filter: isDark ? "brightness(0) invert(1)" : "none" }}
                      />
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={(e) => copyLink(d.id, d.href, e)}
                    data-testid={`mobile-demo-copy-${d.id}`}
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      isDark ? "text-white/60 hover:bg-white/[0.08]" : "text-black/55 hover:bg-black/[0.05]"
                    }`}
                    aria-label="Copy link"
                  >
                    {copiedId === d.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                  <span
                    data-testid={`mobile-demo-preview-${d.id}`}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] font-semibold"
                    style={{ backgroundColor: `${d.accent}20`, color: d.accent }}
                  >
                    <Play className="w-3 h-3" fill="currentColor" /> Preview
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>
        <p className={`mt-6 text-[12px] ${dim}`}>Tap a card to preview it here, without leaving the pitch.</p>
        {previewDemo && <DemoPreviewModal demo={previewDemo} isDark={isDark} onClose={() => setPreviewDemo(null)} />}
      </section>

      {pal && PAL_AFTER_DEMOS.map(([id, C]) => <PalMobileBlock key={id} id={id}><C /></PalMobileBlock>)}

      {!pal && (<>
      {/* Delivery */}
      <section className={`px-6 py-14 border-t ${hairline}`}>
        <div className="k-eyebrow" style={{ color: sectionKicker }}>Delivery</div>
        <h2 className={`mt-3 font-semibold tracking-tight ${ink}`} style={{ fontSize: 28, lineHeight: 1.12, letterSpacing: "-0.024em" }}>
          Delivery you can rely on.
        </h2>
        <p className={`mt-5 text-[15px] leading-relaxed ${muted}`}>
          A dedicated account team, a TPN Gold Certified lab and a workflow trusted by airlines across our group — from onboarding through steady state.
        </p>
      </section>

      {/* Airline proposal entry (private only) */}
      {isPrivate && (
        <section className={`px-6 py-10 border-t ${hairline}`}>
          <a
            href="#your-proposal"
            data-testid="mobile-your-proposal"
            className={`w-full flex items-center gap-4 p-5 rounded-2xl border ${soft}`}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accent}22` }}>
              <ArrowRight className="w-5 h-5" style={{ color: accent }} />
            </div>
            <div className="flex-1">
              <div className={`text-[16px] font-semibold ${ink}`}>Your proposal</div>
              <div className={`text-[13px] mt-0.5 ${muted}`}>Team, scope, creative direction and commercials.</div>
            </div>
          </a>
        </section>
      )}

      {/* Contact */}
      <section className={`px-6 pt-14 pb-10 border-t ${hairline}`}>
        <div className="k-eyebrow" style={{ color: sectionKicker }}>Next step</div>
        <h2 className={`mt-3 font-semibold tracking-tight ${ink}`} style={{ fontSize: 28, lineHeight: 1.12, letterSpacing: "-0.024em" }}>
          Let's talk.
        </h2>
        <p className={`mt-4 text-[15px] leading-relaxed ${muted}`}>
          Ready to explore what Aeroplay could do for your airline?
        </p>
        <a
          href="mailto:hello@aeroplay.app?subject=Aeroplay%20Pitch%20Follow-up"
          data-testid="mobile-contact-btn"
          className={`mt-6 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full text-[15px] font-semibold ${isDark ? "bg-white text-[#0b0b0d]" : "bg-[#1d1d1f] text-white"}`}
        >
          Contact Aeroplay <ArrowRight className="w-4 h-4" />
        </a>
      </section>

      {isPrivate && (
        <section id="your-proposal" className={`px-6 py-14 border-t ${hairline}`}>
          <div className="k-eyebrow" style={{ color: accent }}>Your proposal</div>
          <h2 className={`mt-3 font-semibold tracking-tight ${ink}`} style={{ fontSize: 28, lineHeight: 1.12, letterSpacing: "-0.024em" }}>
            {airline.name}
          </h2>
          <p className={`mt-4 text-[15px] leading-relaxed ${muted}`}>
            Detailed team, scope, creative direction and commercial figures are best reviewed on desktop. Open this link on a laptop for the full presentation, or ask your Aeroplay account manager for a walkthrough.
          </p>
          <div className={`mt-5 rounded-2xl p-4 border ${soft} flex items-center gap-3`}>
            <img src={isDark ? LOGO_DARK : LOGO_LIGHT} alt="Aeroplay" className="h-6 w-auto" />
            <div className={`text-[13px] ${muted}`}>The full proposal loads best on a larger screen.</div>
          </div>
        </section>
      )}

      </>)}

      <footer className={`px-6 py-8 text-center text-[11px] ${muted} border-t ${hairline}`} style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 32px)" }}>
        © Aeroplay Entertainment
      </footer>
    </div>
  );
};

export default MobileJourney;
